/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { Callout } from './Callout';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** `drawer` slides in from the right edge; `dialog` sits in the middle. */
  variant?: 'dialog' | 'drawer';
  /** `alertdialog` for a confirmation that needs an answer. */
  role?: 'dialog' | 'alertdialog';
  className?: string;
  /** Hide the close (X) button (a confirmation answers with its own buttons). */
  hideClose?: boolean;
  /** Keep the title for assistive tech only (the navigation drawer draws its own top). */
  titleHidden?: boolean;
}

/**
 * The native <dialog>, shown modal: the browser keeps focus inside it, Esc closes it, and the page behind is inert.
 * Clicking the scrim closes it too. Focus returns to whatever opened it.
 */
export function Dialog({ open, onClose, title, description, children, footer, variant = 'dialog', role = 'dialog', className, hideClose = false, titleHidden = false }: DialogProps) {
  if (!open) return null;
  return (
    <OpenDialog onClose={onClose} title={title} description={description} footer={footer} variant={variant} role={role} className={className} hideClose={hideClose} titleHidden={titleHidden}>
      {children}
    </OpenDialog>
  );
}

function OpenDialog({ onClose, title, description, children, footer, variant, role, className, hideClose, titleHidden }: Omit<DialogProps, 'open'>) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialog.open) dialog.showModal();
    const onCancel = (event: Event) => {
      event.preventDefault();
      close.current();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      if (dialog.open) dialog.close();
      opener?.focus?.();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={[variant === 'drawer' ? 'ui-drawer' : 'ui-dialog', className ?? ''].filter(Boolean).join(' ')}
      role={role === 'alertdialog' ? 'alertdialog' : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClick={(event) => {
        // A click on the dialog element itself (not its contents) is a click on the scrim around the box.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ui-dialog-box">
        {titleHidden ? (
          <h2 id={titleId} className="ui-sr">{title}</h2>
        ) : (
          <div className="ui-dialog-head">
            <div className="ui-dialog-heading">
              <h2 id={titleId} className="ui-dialog-title">{title}</h2>
              {description ? <p id={descId} className="ui-dialog-desc">{description}</p> : null}
            </div>
            {hideClose ? null : <Button variant="ghost" size="sm" icon={X} aria-label="Close" onClick={onClose} />}
          </div>
        )}
        {children !== undefined && children !== null ? <div className="ui-dialog-body">{children}</div> : null}
        {footer ? <div className="ui-dialog-foot">{footer}</div> : null}
      </div>
    </dialog>
  );
}

export type DrawerProps = Omit<DialogProps, 'variant' | 'role'>;

/** A side panel for editing one thing (a row's roles, a project) without leaving the list. */
export function Drawer(props: DrawerProps) {
  return <Dialog {...props} variant="drawer" />;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  /** What is lost or changed, in plain words. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for anything that removes or cannot be undone. */
  tone?: 'danger' | 'primary';
  busy?: boolean;
  /** The service's refusal, shown inside the dialog so the person can try again or cancel. */
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

/** A confirmation that names what is lost, in place of window.confirm. Cancel comes first, so it has the focus when it opens. */
export function ConfirmDialog({ open, title, children, confirmLabel, cancelLabel = 'Cancel', tone = 'danger', busy = false, error, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title={title}
      role="alertdialog"
      hideClose
      className="is-confirm"
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
          <Button variant={tone === 'danger' ? 'destructive' : 'primary'} onClick={onConfirm} busy={busy}>{confirmLabel}</Button>
        </>
      )}
    >
      <div className="ui-confirm-body">{children}</div>
      {error ? <Callout tone="bad" role="alert">{error}</Callout> : null}
    </Dialog>
  );
}

export interface ConfirmRequest {
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
}

/**
 * `const [confirm, dialog] = useConfirm()`; render `{dialog}`, then `if (await confirm({...})) remove()`. The drop-in
 * for `window.confirm` (nine destructive actions still use it; each moves over as its screen is rebuilt).
 */
export function useConfirm(): [(request: ConfirmRequest) => Promise<boolean>, ReactNode] {
  const [pending, setPending] = useState<{ request: ConfirmRequest; resolve: (answer: boolean) => void } | null>(null);
  const ask = (request: ConfirmRequest) => new Promise<boolean>((resolve) => setPending({ request, resolve }));
  const answer = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };
  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      title={pending?.request.title}
      confirmLabel={pending?.request.confirmLabel ?? 'Confirm'}
      tone={pending?.request.tone}
      onConfirm={() => answer(true)}
      onCancel={() => answer(false)}
    >
      {pending?.request.body}
    </ConfirmDialog>
  );
  return [ask, dialog];
}
